import { Component, useEffect, useState, type ReactNode } from 'react';
import { ReadingMarkdown } from './editor/reading.jsx';

/** If the vault-coupled reading renderer throws for a guest (no booted vault),
 * fall back to the raw markdown so the page never blanks. */
class RenderBoundary extends Component<{ md: string; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed) return <pre className="guest-raw">{this.props.md}</pre>;
    return this.props.children;
  }
}

interface Loaded {
  title: string;
  path: string;
  markdown: string;
  baseHash: string;
  scope: 'read' | 'readwrite';
  expiresAt: number | null;
}

/**
 * Public guest view for a shared note (rendered for /share/:token, bypassing the
 * app + login). Read links get a rendered read-only view; read/write links get a
 * plain-markdown editor that saves back through the scoped guest API.
 */
export function GuestShare({ token }: { token: string }) {
  const [state, setState] = useState<'loading' | 'error' | 'ready'>('loading');
  const [note, setNote] = useState<Loaded | null>(null);
  const [draft, setDraft] = useState('');
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => {
    let stale = false;
    fetch(`/api/guest/${encodeURIComponent(token)}/note`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: Loaded) => {
        if (stale) return;
        setNote(d);
        setDraft(d.markdown);
        setState('ready');
      })
      .catch(() => {
        if (!stale) setState('error');
      });
    return () => {
      stale = true;
    };
  }, [token]);

  if (state === 'loading') {
    return (
      <div className="guest-page">
        <div className="spinner" />
      </div>
    );
  }
  if (state === 'error' || !note) {
    return (
      <div className="guest-page">
        <div className="guest-card">
          <h2>Link unavailable</h2>
          <p>This share link is invalid, has been revoked, or has expired.</p>
        </div>
      </div>
    );
  }

  const save = async () => {
    setSaveMsg('Saving…');
    try {
      const r = await fetch(`/api/guest/${encodeURIComponent(token)}/note`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ content: draft, baseHash: note.baseHash }),
      });
      const d = (await r.json()) as { error?: string; baseHash?: string };
      if (!r.ok) {
        setSaveMsg(d.error || 'Save failed');
        return;
      }
      setNote({ ...note, markdown: draft, baseHash: d.baseHash ?? note.baseHash });
      setSaveMsg('Saved ✓');
    } catch {
      setSaveMsg('Save failed');
    }
  };

  const expiry = note.expiresAt
    ? `Link expires ${new Date(note.expiresAt).toLocaleString()}`
    : 'Link never expires';

  return (
    <div className="guest-page">
      <div className="guest-shell">
        <header className="guest-head">
          <span className="guest-brand">RBV Notes</span>
          <span className="guest-scope">
            {note.scope === 'readwrite' ? 'Shared · can edit' : 'Shared · read-only'}
          </span>
        </header>
        <article className="guest-note">
          <h1>{note.title}</h1>
          {note.scope === 'readwrite' ? (
            <>
              <textarea
                className="guest-editor"
                value={draft}
                spellCheck={false}
                onChange={(e) => setDraft(e.target.value)}
              />
              <div className="guest-actions">
                <button className="guest-save" onClick={() => void save()} disabled={draft === note.markdown}>
                  Save
                </button>
                <span className="guest-savemsg">{saveMsg}</span>
              </div>
            </>
          ) : (
            <RenderBoundary md={note.markdown}>
              <div className="reading-view">
                <ReadingMarkdown md={note.markdown} sourceId="" onNavigate={() => {}} depth={0} />
              </div>
            </RenderBoundary>
          )}
        </article>
        <footer className="guest-foot">{expiry}</footer>
      </div>
    </div>
  );
}
